"use client";

import { Avatar, Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { allRounderIcon, ballIcon, batIcon, keeperIcon } from "../../../Assets";
import { mapHighestLevelToCategory } from "../../../Utils/helperFunctions";
import { resolveCategoryWithFallback } from "../../../Utils/auctionRules";
import { useAuthStore } from "../../../store";

const getCategoryFromSquadItem = (item: any): string => {
  const value =
    item?.category ??
    item?.Category ??
    item?.playerCategory ??
    item?.PlayerCategory ??
    item?.Player_Category__c ??
    item?.Category__c ??
    item?.Player__r?.category ??
    item?.Player__r?.Category ??
    item?.Player__r?.playerCategory ??
    item?.Player__r?.PlayerCategory ??
    item?.Player__r?.Player_Category__c ??
    item?.Player__r?.Category__c ??
    "";
  return String(value).trim();
};

const isNAValue = (value: string): boolean => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  return (
    normalized === "" ||
    normalized === "na" ||
    normalized === "n/a" ||
    normalized === "n.a." ||
    normalized === "-" ||
    normalized === "null" ||
    normalized === "undefined"
  );
};

const resolveSquadCategory = (item: any, division: "men" | "women"): string => {
  const mcaNo =
    item?.MCANo ||
    item?.MCA_Registration_Number__c ||
    item?.MCA_No__c ||
    item?.Player__r?.MCA_Registration_Number__c ||
    item?.Player__r?.MCA_No__c ||
    "";

  const normalizedName = String(
    item?.Player__r?.Name ||
      `${item?.Player__r?.First_Name__c || ""} ${item?.Player__r?.Last_Name__c || ""}`.trim() ||
      item?.Name ||
      "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (normalizedName === "suryansh prashant shedge") {
    return "Senior Player";
  }

  const rawCategory = getCategoryFromSquadItem(item);
  if (!isNAValue(rawCategory)) {
    return resolveCategoryWithFallback({
      division,
      mcaNo,
      fallbackCategory: rawCategory,
    });
  }

  if (division === "women") {
    return resolveCategoryWithFallback({
      division,
      mcaNo,
      fallbackCategory: "General Player",
    });
  }

  const highestLevel =
    item?.Recent_Competitive_Level__c ||
    item?.highest_level_played ||
    item?.Player__r?.Recent_Competitive_Level__c ||
    "";
  const derived = mapHighestLevelToCategory(String(highestLevel));
  if (derived && derived !== "Unknown") {
    return resolveCategoryWithFallback({
      division,
      mcaNo,
      fallbackCategory: derived,
    });
  }

  return resolveCategoryWithFallback({
    division,
    mcaNo,
    fallbackCategory: "Uncategorized",
  });
};

const PlayerCard = ({
  item,
  compactLevel = 0,
  enlarged = false,
  onClick,
}: {
  item: any;
  compactLevel?: number;
  enlarged?: boolean;
  onClick?: () => void;
}) => {
  const playerName = item?.Player__r?.Name || "Player";
  const avatarText = String(playerName).trim().charAt(0).toUpperCase() || "P";
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        cursor: onClick ? "pointer" : "default",
        mb: {
          xs: 1.1,
          md: enlarged
            ? "0.56vw"
            : compactLevel >= 2
              ? "0.24vw"
              : compactLevel >= 1
                ? "0.34vw"
                : "0.5vw",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          gap: {
            xs: 1.2,
            md: enlarged
              ? "0.78vw"
              : compactLevel >= 2
                ? "0.45vw"
                : compactLevel >= 1
                  ? "0.52vw"
                  : "0.65vw",
          },
          px: {
            xs: 1.2,
            md: enlarged
              ? "0.92vw"
              : compactLevel >= 2
                ? "0.56vw"
                : compactLevel >= 1
                  ? "0.64vw"
                  : "0.8vw",
          },
          py: {
            xs: 0.7,
            md: enlarged
              ? "0.5vw"
              : compactLevel >= 2
                ? "0.24vw"
                : compactLevel >= 1
                  ? "0.3vw"
                  : "0.4vw",
          },
          alignItems: "center",
          justifyContent: "space-between",
          border: "0.985px solid var(--gradient, #F97316)",
          background:
            "var(--strawberry, linear-gradient(90deg, rgba(254, 63, 132, 0.25) 0%, rgba(254, 63, 132, 0.07) 89.53%, rgba(254, 63, 132, 0.00) 100%))",
          backdropFilter: "blur(1.9691005945205688px)",
          WebkitBackdropFilter: "blur(1.9691005945205688px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: {
              xs: 1.2,
              md: enlarged
                ? "0.78vw"
                : compactLevel >= 2
                  ? "0.45vw"
                  : compactLevel >= 1
                    ? "0.52vw"
                    : "0.65vw",
            },
            alignItems: "center",
          }}
        >
          <Avatar
            src={
              item?.Player__r?.Photo_URL_1__c ||
              item?.Player__r?.Photo_URL__c ||
              ""
            }
            alt={playerName}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{
              width: enlarged
                ? "2vw"
                : compactLevel >= 2
                  ? "1.35vw"
                  : compactLevel >= 1
                    ? "1.55vw"
                    : "1.8vw",
              height: enlarged
                ? "2vw"
                : compactLevel >= 2
                  ? "1.35vw"
                  : compactLevel >= 1
                    ? "1.55vw"
                    : "1.8vw",
              minWidth: enlarged
                ? 30
                : compactLevel >= 2
                  ? 20
                  : compactLevel >= 1
                    ? 24
                    : 30,
              minHeight: enlarged
                ? 30
                : compactLevel >= 2
                  ? 20
                  : compactLevel >= 1
                    ? 24
                    : 30,
              fontSize: {
                xs: "0.9rem",
                md: compactLevel >= 2 ? "0.62vw" : "0.74vw",
              },
              fontWeight: 700,
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
            }}
          >
            {avatarText}
          </Avatar>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#FFFFFF",
              fontSize: {
                xs: "1.2rem",
                md: enlarged
                  ? "1.45vw"
                  : compactLevel >= 2
                    ? "1.05vw"
                    : compactLevel >= 1
                      ? "1.16vw"
                      : "1.35vw",
              },
              fontStyle: "italic",
              lineHeight: 1.05,
              textTransform: "uppercase",
            }}
          >
            {item?.Player__r?.Name}
          </Typography>
        </Box>
        <img
          src={
            item?.Primary_Role__c === "Bowler"
              ? ballIcon
              : item?.Primary_Role__c === "Wicketkeeper"
                ? keeperIcon
                : item?.Primary_Role__c === "All - rounder"
                  ? allRounderIcon
                  : batIcon
          }
          alt="icon"
          style={{
            width: enlarged
              ? "1.35vw"
              : compactLevel >= 2
                ? "0.95vw"
                : compactLevel >= 1
                  ? "1.08vw"
                  : "1.25vw",
            height: enlarged
              ? "1.35vw"
              : compactLevel >= 2
                ? "0.95vw"
                : compactLevel >= 1
                  ? "1.08vw"
                  : "1.25vw",
            minWidth: enlarged
              ? 20
              : compactLevel >= 2
                ? 14
                : compactLevel >= 1
                  ? 16
                  : 20,
            minHeight: enlarged
              ? 20
              : compactLevel >= 2
                ? 14
                : compactLevel >= 1
                  ? 16
                  : 20,
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
  );
};

const TeamSquadList = ({
  teamDetailsData,
  allPlayers = [],
  division = "men",
  onPlayerSelect,
  onBack,
}: {
  teamDetailsData: any;
  allPlayers?: any[];
  division?: "men" | "women";
  onPlayerSelect?: (playerData: any) => void;
  onBack?: () => void;
}) => {
  const { setSelectedPlayerData } = useAuthStore();

  const normalizedPlayers = useMemo(
    () =>
      allPlayers.map((p: any) => ({
        raw: p,
        playerId: String(p?.playerId || p?.id || p?.Id || "").trim(),
        mcaNo: String(
          p?.MCANo || p?.MCA_No__c || p?.MCA_Registration_Number__c || "",
        ).trim(),
        name: String(p?.name || p?.Name || "")
          .trim()
          .toLowerCase(),
      })),
    [allPlayers],
  );

  const handlePlayerClick = (item: any) => {
    const playerInfo = item?.Player__r || {};
    const playerId = String(
      item?.playerId ||
        item?.Player__c ||
        playerInfo?.playerId ||
        playerInfo?.Id ||
        "",
    ).trim();
    const mcaNo = String(
      item?.MCANo ||
        item?.MCA_Registration_Number__c ||
        item?.MCA_No__c ||
        playerInfo?.MCA_Registration_Number__c ||
        playerInfo?.MCA_No__c ||
        "",
    ).trim();
    const fullName = String(
      playerInfo?.Name ||
        `${playerInfo?.First_Name__c || ""} ${playerInfo?.Last_Name__c || ""}`.trim() ||
        item?.Name ||
        "",
    )
      .trim()
      .toLowerCase();

    const matchedPlayerFromApi =
      normalizedPlayers.find((p) => p.playerId && p.playerId === playerId)
        ?.raw ||
      normalizedPlayers.find((p) => p.mcaNo && p.mcaNo === mcaNo)?.raw ||
      normalizedPlayers.find((p) => p.name && p.name === fullName)?.raw ||
      null;

    const parseMaybeJson = (value: any) => {
      if (!value) return null;
      if (typeof value === "object") return value;
      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      }
      return null;
    };

    const detailsCandidates = [
      matchedPlayerFromApi?.playerDetails,
      matchedPlayerFromApi?.Player_Details__r,
      parseMaybeJson(matchedPlayerFromApi?.Player_Details__c),
      playerInfo?.playerDetails,
      item?.playerDetails,
      playerInfo?.Player_Details__r,
      item?.Player_Details__r,
      parseMaybeJson(playerInfo?.Player_Details__c),
      parseMaybeJson(item?.Player_Details__c),
    ].filter(Boolean);

    const statsCandidates = [
      matchedPlayerFromApi?.stats,
      matchedPlayerFromApi?.playerDetails?.stats,
      matchedPlayerFromApi?.Player_Stats__r?.records,
      playerInfo?.stats,
      item?.stats,
      playerInfo?.Player_Stats__r?.records,
      item?.Player_Stats__r?.records,
      ...detailsCandidates.map((d: any) => d?.stats),
    ];

    const resolvedStats = statsCandidates.find((entry) => Array.isArray(entry));
    const resolvedPlayerDetails =
      detailsCandidates[0] ||
      (Array.isArray(resolvedStats) ? { stats: resolvedStats } : {});

    if (
      Array.isArray(resolvedStats) &&
      !Array.isArray(resolvedPlayerDetails?.stats)
    ) {
      resolvedPlayerDetails.stats = resolvedStats;
    }

    const rawBasePrice =
      item?.basePrice ||
      matchedPlayerFromApi?.basePrice ||
      playerInfo?.basePrice ||
      "-";

    const categoryFromKey = resolveSquadCategory(item, division);

    const playerData = {
      auctionResult: "Stats",
      name:
        playerInfo?.Name ||
        `${playerInfo?.First_Name__c || ""} ${playerInfo?.Last_Name__c || ""}`.trim() ||
        item?.Name ||
        "-",
      firstName: playerInfo?.First_Name__c || playerInfo?.FirstName || "",
      lastName: playerInfo?.Last_Name__c || playerInfo?.LastName || "",
      city:
        playerInfo?.City__c ||
        playerInfo?.Current_City__c ||
        item?.City__c ||
        item?.currentCity ||
        "-",
      currentCity:
        playerInfo?.Current_City__c ||
        playerInfo?.City__c ||
        item?.currentCity ||
        item?.City__c ||
        "-",
      MCANo:
        item?.MCANo ||
        item?.MCA_Registration_Number__c ||
        item?.MCA_No__c ||
        playerInfo?.MCA_Registration_Number__c ||
        playerInfo?.MCA_No__c ||
        "-",
      roleType:
        item?.Primary_Role__c ||
        playerInfo?.Primary_Role__c ||
        playerInfo?.Role__c ||
        "-",
      role:
        item?.Primary_Role__c ||
        playerInfo?.Primary_Role__c ||
        playerInfo?.Role__c ||
        "-",
      playerBirthDate:
        item?.birthData ||
        item?.Birth_Date__c ||
        item?.Date_of_Birth__c ||
        playerInfo?.Birth_Date__c ||
        playerInfo?.Date_of_Birth__c,
      birthData:
        item?.birthData ||
        item?.Birth_Date__c ||
        item?.Date_of_Birth__c ||
        playerInfo?.Birth_Date__c ||
        playerInfo?.Date_of_Birth__c,
      playerAge:
        item?.playerAge ||
        item?.Player_Age__c ||
        item?.Age__c ||
        playerInfo?.Player_Age__c,
      winningBid:
        item?.winningBid ??
        item?.Winning_Bid__c ??
        matchedPlayerFromApi?.winningBid ??
        matchedPlayerFromApi?.Winning_Bid__c ??
        playerInfo?.winningBid ??
        playerInfo?.Winning_Bid__c,
      basePrice: rawBasePrice,
      highest_level_played:
        item?.Recent_Competitive_Level__c ||
        playerInfo?.Recent_Competitive_Level__c ||
        "",
      category: categoryFromKey,
      playerCategory: categoryFromKey,
      photo: playerInfo?.Photo_URL_1__c || playerInfo?.Photo_URL__c || "",
      playerDetails: resolvedPlayerDetails,
    };
    if (onPlayerSelect) {
      onPlayerSelect(playerData);
      return;
    }
    setSelectedPlayerData(playerData);
  };

  const groupedPlayers = Array.isArray(teamDetailsData)
    ? teamDetailsData.reduce((acc: any, item: any) => {
        const category = resolveSquadCategory(item, division);
        if (!acc[category]) acc[category] = [];
        acc[category].push(item);
        return acc;
      }, {})
    : {};
  const listBuckets =
    division === "women"
      ? ["Icon Player", "Under-19 Player", "General Player"]
      : [
          "Icon Player",
          "Senior Player",
          "Emerging Player",
          "Development Player",
        ];
  const maxPlayersInAColumn = listBuckets.reduce(
    (maxCount, key) => Math.max(maxCount, groupedPlayers[key]?.length || 0),
    0,
  );
  const compactLevel =
    maxPlayersInAColumn >= 14 ? 2 : maxPlayersInAColumn >= 11 ? 1 : 0;
  const womenGeneralCount = groupedPlayers["General Player"]?.length || 0;
  const useExpandedGeneralGrid =
    division === "women" && womenGeneralCount >= 11;
  const knownBuckets = new Set(listBuckets);
  const extraBucketKeys = Object.keys(groupedPlayers).filter(
    (key) => !knownBuckets.has(key),
  );
  const headingFontSize = useExpandedGeneralGrid
    ? { xs: "1.2rem", md: "2vw" }
    : compactLevel >= 2
      ? { xs: "1.2rem", md: "1.45vw" }
      : compactLevel >= 1
        ? { xs: "1.2rem", md: "1.62vw" }
        : { xs: "1.2rem", md: "1.9vw" };
  const headingMarginBottom =
    compactLevel >= 2
      ? { xs: 1.2, md: "0.3vw" }
      : compactLevel >= 1
        ? { xs: 1.2, md: "0.42vw" }
        : { xs: 1.2, md: "0.6vw" };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: useExpandedGeneralGrid ? "1fr 1fr 1fr" : "1fr 1fr 1fr",
        },
        gap: { xs: 2.2, md: "1.2vw" },
        width: "100%",
        mt: { xs: 2, md: "1vw" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {groupedPlayers["Icon Player"]?.length > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: "0.55vw" },
                mb: headingMarginBottom,
              }}
            >
              <Typography
                variant="h4"
                color="white"
                sx={{
                  fontSize: headingFontSize,
                  fontWeight: 700,
                  mb: 0,
                }}
              >
                ICON PLAYER ({groupedPlayers["Icon Player"].length})
              </Typography>
            </Box>
            {groupedPlayers["Icon Player"].map((item: any, i: number) => (
              <PlayerCard
                item={item}
                compactLevel={compactLevel}
                enlarged={useExpandedGeneralGrid}
                onClick={() => handlePlayerClick(item)}
                key={`icon-${i}`}
              />
            ))}
          </>
        )}

        {division !== "women" &&
          groupedPlayers["Senior Player"]?.length > 0 && (
            <>
              <Typography
                variant="h4"
                color="white"
                sx={{
                  fontSize: headingFontSize,
                  fontWeight: 700,
                  mb: headingMarginBottom,
                  mt: { xs: 2.2, md: "1.1vw" },
                }}
              >
                SENIOR PLAYERS ({groupedPlayers["Senior Player"].length})
              </Typography>
              {groupedPlayers["Senior Player"].map((item: any, i: number) => (
                <PlayerCard
                  item={item}
                  compactLevel={compactLevel}
                  enlarged={useExpandedGeneralGrid}
                  onClick={() => handlePlayerClick(item)}
                  key={`senior-${i}`}
                />
              ))}
            </>
          )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {division === "women"
          ? groupedPlayers["Under-19 Player"]?.length > 0 && (
              <>
                <Typography
                  variant="h4"
                  color="white"
                  sx={{
                    fontSize: headingFontSize,
                    fontWeight: 700,
                    mb: headingMarginBottom,
                  }}
                >
                  UNDER-19 PLAYERS ({groupedPlayers["Under-19 Player"].length})
                </Typography>
                {groupedPlayers["Under-19 Player"].map(
                  (item: any, i: number) => (
                    <PlayerCard
                      item={item}
                      compactLevel={compactLevel}
                      enlarged={useExpandedGeneralGrid}
                      onClick={() => handlePlayerClick(item)}
                      key={`under19-${i}`}
                    />
                  ),
                )}
              </>
            )
          : groupedPlayers["Emerging Player"]?.length > 0 && (
              <>
                <Typography
                  variant="h4"
                  color="white"
                  sx={{
                    fontSize: headingFontSize,
                    fontWeight: 700,
                    mb: headingMarginBottom,
                  }}
                >
                  EMERGING PLAYERS ({groupedPlayers["Emerging Player"].length})
                </Typography>
                {groupedPlayers["Emerging Player"].map(
                  (item: any, i: number) => (
                    <PlayerCard
                      item={item}
                      compactLevel={compactLevel}
                      enlarged={useExpandedGeneralGrid}
                      onClick={() => handlePlayerClick(item)}
                      key={`emerging-${i}`}
                    />
                  ),
                )}
              </>
            )}
      </Box>

      <Box
        sx={{
          minWidth: 0,
          gridColumn: {
            xs: "auto",
            md: useExpandedGeneralGrid ? "1 / span 3" : "auto",
          },
        }}
      >
        {division === "women"
          ? groupedPlayers["General Player"]?.length > 0 && (
              <>
                <Typography
                  variant="h4"
                  color="white"
                  sx={{
                    fontSize: headingFontSize,
                    fontWeight: 700,
                    mb: headingMarginBottom,
                  }}
                >
                  GENERAL PLAYERS ({groupedPlayers["General Player"].length})
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: useExpandedGeneralGrid ? "1fr 1fr" : "1fr",
                    },
                    columnGap: {
                      xs: 0,
                      md: useExpandedGeneralGrid ? "0.8vw" : 0,
                    },
                  }}
                >
                  {groupedPlayers["General Player"].map(
                    (item: any, i: number) => (
                      <PlayerCard
                        item={item}
                        compactLevel={useExpandedGeneralGrid ? 0 : compactLevel}
                        enlarged={useExpandedGeneralGrid}
                        onClick={() => handlePlayerClick(item)}
                        key={`general-${i}`}
                      />
                    ),
                  )}
                </Box>
              </>
            )
          : groupedPlayers["Development Player"]?.length > 0 && (
              <>
                <Typography
                  variant="h4"
                  color="white"
                  sx={{
                    fontSize: headingFontSize,
                    fontWeight: 700,
                    mb: headingMarginBottom,
                    whiteSpace: "nowrap",
                  }}
                >
                  DEVELOPMENT PLAYERS (
                  {groupedPlayers["Development Player"].length})
                </Typography>
                {groupedPlayers["Development Player"].map(
                  (item: any, i: number) => (
                    <PlayerCard
                      item={item}
                      compactLevel={compactLevel}
                      enlarged={useExpandedGeneralGrid}
                      onClick={() => handlePlayerClick(item)}
                      key={`dev-${i}`}
                    />
                  ),
                )}
              </>
            )}
      </Box>

      {extraBucketKeys.length > 0 && (
        <Box sx={{ minWidth: 0, gridColumn: { xs: "auto", md: "1 / span 3" } }}>
          {extraBucketKeys.map((bucketKey) => (
            <Box
              key={`extra-${bucketKey}`}
              sx={{ mb: { xs: 2.2, md: "1.2vw" } }}
            >
              <Typography
                variant="h4"
                color="white"
                sx={{
                  fontSize: headingFontSize,
                  fontWeight: 700,
                  mb: headingMarginBottom,
                }}
              >
                {bucketKey.toUpperCase()} (
                {groupedPlayers[bucketKey]?.length || 0})
              </Typography>
              {groupedPlayers[bucketKey].map((item: any, i: number) => (
                <PlayerCard
                  item={item}
                  compactLevel={compactLevel}
                  enlarged={useExpandedGeneralGrid}
                  onClick={() => handlePlayerClick(item)}
                  key={`extra-${bucketKey}-${i}`}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TeamSquadList;
